import { getDiscordClient } from './discord';
import { GameState, CardInstance } from '../types';

// ペアリング同期用
export interface MatchPairingSync {
  player1Id: string;
  player2Id: string;
  firstPlayerId: string;
  matchIndex: number;
}

// Bot同期用
export interface BotPlayerSync {
  id: string;
  name: string;
  difficulty: string;
}

// サーバーのルーム状態
export interface ServerRoomState {
  roomId: string;
  players: ServerPlayerState[];
  phase: string;
  round: number;
  lastUpdate: number;
  hostId: string;
  // ペアリング情報
  pairings?: MatchPairingSync[];
  bots?: BotPlayerSync[];
}

export interface ServerPlayerState {
  id: string;
  name: string;
  isReady: boolean;
  deckHash?: string;
  lastAction?: string;
  lastActionTime?: number;
  isSpectator?: boolean;
}

// 同期用カードインスタンス
export interface CardInstanceSync {
  defId: string;
  instanceId: string;
  currentPower: number;
  isRevealed: boolean;
}

// 同期用ベンチスロット
export interface BenchSlotSync {
  defId: string;
  cards: CardInstanceSync[];
}

// 同期用プレイヤー盤面
export interface PlayerFieldSync {
  deck: CardInstanceSync[];
  attackStack: CardInstanceSync[];
  benchSlots: BenchSlotSync[];
  flagHolder: CardInstanceSync | null;
  exile: CardInstanceSync[];
  isAttacker: boolean;
}

// マッチ同期用の状態 (完全な盤面情報を含む)
export interface MatchSyncState {
  matchId: string;
  player1Id: string;
  player2Id: string;
  // 完全な盤面状態
  player1Field: PlayerFieldSync;
  player2Field: PlayerFieldSync;
  // 互換性のため残す(初期デッキ用)
  player1Deck: CardInstanceSync[];
  player2Deck: CardInstanceSync[];
  firstPlayerId: string;
  turnCount: number;
  currentAttackerId: string;
  status: 'waiting' | 'inProgress' | 'finished';
  lastAction: MatchAction | null;
  lastActionTime: number;
  winner?: string;
  // 待機中フラグ (swap/move処理中)
  isWaitingForInput?: boolean;
  // ラウンド番号 (新しいマッチの判定用)
  round?: number;
  // マッチインデックス (複数マッチ対応)
  matchIndex?: number;
  // ログ (新しく追加されたログのみ)
  recentLogs?: { turn: number; action: string; playerId: string; cardId?: string; message: string }[];
}

export interface MatchAction {
  type: 'start' | 'draw' | 'end' | 'sync';
  playerId: string;
  data?: unknown;
  timestamp: number;
}

export interface MultiplayerCallbacks {
  onPlayerJoin: (player: { id: string; name: string }) => void;
  onPlayerLeave: (playerId: string) => void;
  onGameStart: () => void;
  onStateSync: (state: Partial<GameState>) => void;
  onDeckPhaseComplete: (playerId: string) => void;
  onAllPlayersReady: () => void;
  onRoomStateUpdate: (state: ServerRoomState) => void;
}

// API Base URL
const API_BASE = '/api/room';

// マルチプレイ同期マネージャー
export class MultiplayerManager {
  private isHost: boolean = false;
  private isSpectator: boolean = false;
  private roomId: string | null = null;
  private callbacks: Partial<MultiplayerCallbacks> = {};
  private pollAbortController: AbortController | null = null;
  private isPolling: boolean = false;
  private lastUpdateTime: number = 0;
  private currentUserId: string | null = null;
  private knownPlayerIds: Set<string> = new Set();
  private knownReadyIds: Set<string> = new Set();

  constructor() {
    console.log('[MP] MultiplayerManager initialized');
  }

  // ルーム作成/参加
  async createRoom(): Promise<string> {
    const discord = getDiscordClient();
    const user = discord.getUser();
    
    if (!user) throw new Error('Not authenticated');

    // Discord instance_idからchannel部分を抽出してルームIDとして使用
    // instance_id形式: i-{instance}-gc-{channel_id}-{guild_id}
    const urlParams = new URLSearchParams(window.location.search);
    const instanceId = urlParams.get('instance_id') || '';
    const channelId = urlParams.get('channel_id');
    
    // channel_idがあればそれを使用、なければinstance_idから抽出
    let roomId: string;
    if (channelId) {
      roomId = `room_${channelId}`;
    } else if (instanceId.includes('-gc-')) {
      // instance_idからgc以降を抽出
      const gcPart = instanceId.split('-gc-')[1];
      roomId = `room_${gcPart}`;
    } else {
      roomId = `local_${Date.now()}`;
    }
    
    this.roomId = roomId;
    this.currentUserId = user.id;

    console.log('[MP] Creating/joining room:', this.roomId, '(instance:', instanceId, ')');

    // まずルーム状態を確認
    const stateResponse = await fetch(`${API_BASE}/${this.roomId}/state`);
    const existingState = await stateResponse.json();
    
    // 既存ルームがあるか確認
    const hasExistingRoom = existingState && existingState.players && existingState.players.length > 0;
    
    // Activity起動者 (最初に入る人) は常にサーバーをリセット
    // 条件: ルームが存在しない、または自分がホスト、または全プレイヤーが非アクティブ
    const shouldReset = !hasExistingRoom || 
      existingState.hostId === user.id ||
      (existingState.players && this.isAllPlayersInactive(existingState.players));
    
    if (shouldReset) {
      console.log('[MP] Resetting room (first entry or host or all inactive)');
      const resetResponse = await fetch(`${API_BASE}/${this.roomId}/reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          playerId: user.id,
          playerName: user.globalName || user.username,
        }),
      });
      
      const resetResult = await resetResponse.json();
      console.log('[MP] Reset response:', resetResponse.status, resetResponse.ok, resetResult);
      if (resetResponse.ok) {
        console.log('[MP] Room reset successful');
        this.isHost = true;
        this.lastUpdateTime = resetResult.state?.lastUpdate || 0;
        this.knownPlayerIds.clear();
        this.knownPlayerIds.add(user.id);
        return this.roomId;
      }
    }

    // 通常の参加処理
    const response = await fetch(`${API_BASE}/${this.roomId}/join`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        playerId: user.id,
        playerName: user.globalName || user.username,
      }),
    });

    const result = await response.json();
    
    if (!response.ok) {
      console.error('[MP] Join failed:', result);
      throw new Error(result.error || 'Join failed');
    }

    console.log('[MP] Joined room:', result);
    this.isHost = result.isHost;
    this.isSpectator = result.isSpectator || false;
    this.lastUpdateTime = result.state?.lastUpdate || 0;
    
    // 既存プレイヤーを記録
    if (result.state?.players) {
      result.state.players.forEach((p: ServerPlayerState) => {
        this.knownPlayerIds.add(p.id);
      });
    }

    return this.roomId;
  }

  // ルーム参加 (既存ルーム)
  async joinRoom(roomId: string): Promise<void> {
    const discord = getDiscordClient();
    const user = discord.getUser();
    
    if (!user) throw new Error('Not authenticated');

    this.roomId = roomId;
    this.currentUserId = user.id;

    const response = await fetch(`${API_BASE}/${roomId}/join`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        playerId: user.id,
        playerName: user.globalName || user.username,
      }),
    });

    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.error || 'Join failed');
    }

    this.isHost = result.isHost;
    this.lastUpdateTime = result.state?.lastUpdate || 0;
  }

  // ポーリング開始
  startListening(): void {
    if (this.isPolling || !this.roomId) return;

    console.log('[MP] Starting polling for room:', this.roomId);
    this.isPolling = true;
    this.poll();
  }

  // ロングポーリング
  private async poll(): Promise<void> {
    if (!this.isPolling || !this.roomId) return;

    this.pollAbortController = new AbortController();

    try {
      const response = await fetch(
        `${API_BASE}/${this.roomId}/poll?since=${this.lastUpdateTime}&timeout=25000`,
        { signal: this.pollAbortController.signal }
      );

      if (!response.ok) {
        console.error('[MP] Poll error:', response.status);
        await this.delay(2000);
      } else {
        const result = await response.json();
        
        if (result.updated && result.state) {
          console.log('[MP] State updated:', result.state);
          this.handleStateUpdate(result.state);
          this.lastUpdateTime = result.state.lastUpdate;
        }
      }
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        console.error('[MP] Poll error:', err);
        await this.delay(2000);
      }
    }

    // 継続してポーリング
    if (this.isPolling) {
      this.poll();
    }
  }

  // 状態更新ハンドラ
  private lastKnownPhase: string = '';
  
  private handleStateUpdate(state: ServerRoomState): void {
    // フェーズ変更検出 - ready状態をリセット
    if (this.lastKnownPhase && this.lastKnownPhase !== state.phase) {
      console.log('[MP] Phase changed:', this.lastKnownPhase, '->', state.phase);
      this.knownReadyIds.clear();
    }
    this.lastKnownPhase = state.phase;
    
    // サーバーのready状態と同期 (サーバーでリセットされた場合に対応)
    // サーバー上でisReady=falseなのにknownReadyIdsに残っている場合は削除
    for (const player of state.players) {
      if (!player.isReady && this.knownReadyIds.has(player.id)) {
        console.log('[MP] Player ready state reset on server:', player.id);
        this.knownReadyIds.delete(player.id);
      }
    }
    
    // プレイヤー参加検出
    const currentPlayerIds = new Set(state.players.map(p => p.id));
    
    for (const player of state.players) {
      if (!this.knownPlayerIds.has(player.id)) {
        console.log('[MP] New player joined:', player);
        this.callbacks.onPlayerJoin?.({ id: player.id, name: player.name });
        this.knownPlayerIds.add(player.id);
      }
    }

    // プレイヤー退出検出
    for (const id of this.knownPlayerIds) {
      if (!currentPlayerIds.has(id)) {
        console.log('[MP] Player left:', id);
        this.callbacks.onPlayerLeave?.(id);
        this.knownPlayerIds.delete(id);
      }
    }

    // 準備完了したプレイヤーを通知 (新しくreadyになった場合のみ)
    for (const player of state.players) {
      if (player.isReady && !this.knownReadyIds.has(player.id)) {
        console.log('[MP] Player became ready:', player.id);
        this.knownReadyIds.add(player.id);
        this.callbacks.onDeckPhaseComplete?.(player.id);
      }
    }

    // コールバック呼び出し
    this.callbacks.onRoomStateUpdate?.(state);

    // 全員準備完了チェック (全員が新しくreadyになった場合のみ)
    const allReady = state.players.length >= 2 && state.players.every(p => p.isReady);
    const allKnownReady = state.players.every(p => this.knownReadyIds.has(p.id));
    if (allReady && allKnownReady) {
      console.log('[MP] All players ready!');
      this.callbacks.onAllPlayersReady?.();
    }
  }

  // 準備完了通知
  async notifyReady(isReady: boolean = true): Promise<{ allReady: boolean }> {
    if (!this.roomId || !this.currentUserId) {
      console.error('[MP] Cannot notify ready: no room or user');
      return { allReady: false };
    }

    console.log('[MP] Notifying ready:', isReady);

    const response = await fetch(`${API_BASE}/${this.roomId}/ready`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        playerId: this.currentUserId,
        isReady,
      }),
    });

    if (!response.ok) {
      console.error('[MP] Ready notification failed');
      return { allReady: false };
    }

    const result = await response.json();
    return { allReady: result.allReady };
  }

  // デッキフェーズ完了通知 (notifyReadyのエイリアス)
  async notifyDeckPhaseComplete(): Promise<{ allReady: boolean }> {
    return this.notifyReady(true);
  }

  // ローカルのready状態をリセット
  resetReadyState(): void {
    console.log('[MP] Resetting local ready state');
    this.knownReadyIds.clear();
  }

  // サーバーのready状態をリセット (フェーズ変更時)
  async resetServerReadyState(): Promise<boolean> {
    if (!this.roomId || !this.currentUserId) {
      console.log('[MP] resetServerReadyState: no room or user');
      return false;
    }
    
    if (!this.isHost) {
      console.log('[MP] resetServerReadyState: not host, skipping');
      return false;
    }
    
    console.log('[MP] Resetting server ready state');
    
    // 全プレイヤーのready状態をfalseに
    const response = await fetch(`${API_BASE}/${this.roomId}/phase`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        playerId: this.currentUserId,
        phase: 'starterSelect',
        round: 0,
      }),
    });
    
    const result = await response.json();
    console.log('[MP] Reset server ready result:', result);
    
    this.knownReadyIds.clear();
    return response.ok;
  }

  // フェーズ更新 (ホストのみ)
  async updatePhase(phase: string, round?: number): Promise<boolean> {
    if (!this.roomId || !this.currentUserId) return false;
    if (!this.isHost) {
      console.warn('[MP] Only host can update phase');
      return false;
    }

    console.log('[MP] Updating phase:', phase, round);
    
    // フェーズ変更時はready状態をリセット
    this.knownReadyIds.clear();

    const response = await fetch(`${API_BASE}/${this.roomId}/phase`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        playerId: this.currentUserId,
        phase,
        round,
      }),
    });

    return response.ok;
  }

  // ルーム状態取得
  async getRoomState(): Promise<ServerRoomState | null> {
    if (!this.roomId) return null;

    const response = await fetch(`${API_BASE}/${this.roomId}/state`);
    
    if (!response.ok) return null;
    
    return await response.json();
  }

  // ルーム退出
  async leaveRoom(): Promise<void> {
    if (!this.roomId || !this.currentUserId) return;

    await fetch(`${API_BASE}/${this.roomId}/leave`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ playerId: this.currentUserId }),
    });
  }

  // コールバック設定
  setCallbacks(callbacks: Partial<MultiplayerCallbacks>): void {
    this.callbacks = { ...this.callbacks, ...callbacks };
  }

  // ホストかどうか
  getIsHost(): boolean {
    return this.isHost;
  }

  // 観戦者かどうか
  getIsSpectator(): boolean {
    return this.isSpectator;
  }

  // ルームID取得
  getRoomId(): string | null {
    return this.roomId;
  }

  // 現在のユーザーID
  getCurrentUserId(): string | null {
    return this.currentUserId;
  }

  // クリーンアップ
  destroy(): void {
    console.log('[MP] Destroying MultiplayerManager');
    this.isPolling = false;
    this.pollAbortController?.abort();
    this.leaveRoom();
  }

  // ユーティリティ
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // 全プレイヤーが非アクティブかチェック (5分以上更新なし)
  private isAllPlayersInactive(players: { lastActionTime?: number }[]): boolean {
    if (!players || players.length === 0) return true;
    
    const INACTIVE_THRESHOLD = 5 * 60 * 1000; // 5分
    const now = Date.now();
    
    return players.every(p => {
      const lastAction = p.lastActionTime || 0;
      return (now - lastAction) > INACTIVE_THRESHOLD;
    });
  }

  // ゲーム開始通知 (ホストのみ)
  async broadcastGameStart(): Promise<void> {
    if (!this.isHost) return;
    await this.updatePhase('starterSelect', 0);
  }

  // 状態同期 (下位互換性のため残す)
  syncState(_state: Partial<GameState>): void {
    console.log('[MP] syncState called (no-op in KV mode)');
  }

  // チャット送信 (将来の拡張用)
  sendChat(_text: string): void {
    console.log('[MP] sendChat not implemented in KV mode');
  }

  // 参加者リスト取得
  async getParticipants(): Promise<{ id: string; username: string }[]> {
    const state = await this.getRoomState();
    if (!state) return [];
    return state.players.map(p => ({ id: p.id, username: p.name }));
  }

  // ========== マッチ同期メソッド ==========

  // マッチ状態を作成 (ホストのみ)
  async createMatchState(matchData: MatchSyncState, matchIndex: number = 0): Promise<MatchSyncState | null> {
    if (!this.roomId || !this.currentUserId) {
      console.error('[MP] Cannot create match: no room or user');
      return null;
    }

    // マッチ作成はplayer1が担当 (isHostチェックを削除)
    console.log('[MP] Creating match state for matchIndex:', matchIndex);

    const response = await fetch(`${API_BASE}/${this.roomId}/match`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        playerId: this.currentUserId,
        matchData,
        matchIndex,
      }),
    });

    if (!response.ok) {
      console.error('[MP] Failed to create match');
      return null;
    }

    const result = await response.json();
    return result.match;
  }

  // マッチ状態を取得
  async getMatchState(matchIndex: number = 0): Promise<MatchSyncState | null> {
    if (!this.roomId) return null;

    const response = await fetch(`${API_BASE}/${this.roomId}/match?matchIndex=${matchIndex}`);
    
    if (!response.ok) return null;
    
    const result = await response.json();
    return result.match;
  }

  // マッチ状態が準備できるまで待機 (新しいマッチであることを確認)
  async waitForMatchState(maxWaitMs: number = 5000, expectedTurnCount: number = 0, expectedRound?: number, matchIndex: number = 0): Promise<MatchSyncState | null> {
    const startTime = Date.now();
    const checkIntervalMs = 1500;  // 1.5秒間隔 (KV API節約)

    while (Date.now() - startTime < maxWaitMs) {
      const match = await this.getMatchState(matchIndex);
      // 新しいマッチ (turnCount === expectedTurnCount) であることを確認
      // ラウンド番号が指定されている場合はそれもチェック
      if (match && match.turnCount === expectedTurnCount) {
        if (expectedRound === undefined || match.round === expectedRound) {
          console.log('[MP] Match state received, turnCount:', match.turnCount, 'round:', match.round);
          return match;
        }
        console.log('[MP] Waiting for correct round, current:', match.round, 'expected:', expectedRound);
      }
      if (match) {
        console.log('[MP] Waiting for new match, current turnCount:', match.turnCount, 'expected:', expectedTurnCount);
      }
      await this.delay(checkIntervalMs);
    }

    console.warn('[MP] Timeout waiting for match state');
    return null;
  }

  // マッチアクション送信 (カードめくり通知)
  async sendMatchAction(action: MatchAction): Promise<boolean> {
    if (!this.roomId || !this.currentUserId) return false;

    console.log('[MP] Sending match action:', action.type);

    const response = await fetch(`${API_BASE}/${this.roomId}/match-action`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        playerId: this.currentUserId,
        action,
      }),
    });

    return response.ok;
  }

  // マッチ状態を更新 (ターン処理後に完全な盤面状態を保存)
  async updateMatchState(matchState: MatchSyncState): Promise<boolean> {
    if (!this.roomId || !this.currentUserId) return false;

    const matchIndex = matchState.matchIndex || 0;
    console.log('[MP] Updating match state, turn:', matchState.turnCount, 'waiting:', matchState.isWaitingForInput, 'matchIndex:', matchIndex);

    const response = await fetch(`${API_BASE}/${this.roomId}/match-action`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        playerId: this.currentUserId,
        action: {
          type: 'sync',
          playerId: this.currentUserId,
          timestamp: Date.now(),
        },
        matchState,
        matchIndex,
      }),
    });

    return response.ok;
  }

  // マッチ状態の変更をポーリング
  async pollMatchUpdate(lastActionTime: number, timeoutMs: number = 10000, matchIndex: number = 0): Promise<MatchSyncState | null> {
    if (!this.roomId) return null;

    const response = await fetch(
      `${API_BASE}/${this.roomId}/match-poll?since=${lastActionTime}&timeout=${timeoutMs}&matchIndex=${matchIndex}`
    );

    if (!response.ok) return null;

    const result = await response.json();
    if (result.updated && result.match) {
      return result.match;
    }
    return null;
  }

  // 相手のターン完了を待機 (入力待ち状態でないことを確認)
  async waitForOpponentTurn(lastActionTime: number, maxWaitMs: number = 30000, matchIndex: number = 0): Promise<MatchSyncState | null> {
    const startTime = Date.now();

    while (Date.now() - startTime < maxWaitMs) {
      const match = await this.pollMatchUpdate(lastActionTime, 5000, matchIndex);
      if (match && match.lastActionTime > lastActionTime) {
        // 入力待ち状態でなければ完了
        if (match.isWaitingForInput !== true) {
          console.log('[MP] Opponent turn completed');
          return match;
        }
        console.log('[MP] Opponent is waiting for input, continue polling');
      }
    }

    console.warn('[MP] Timeout waiting for opponent turn');
    return null;
  }

  // ========== マッチ完了同期メソッド (3人以上用) ==========

  // マッチ完了を通知
  async notifyMatchComplete(totalMatchCount: number = 0): Promise<{ allComplete: boolean; completedCount: number; expectedCount: number }> {
    if (!this.roomId || !this.currentUserId) {
      console.error('[MP] Cannot notify match complete: no room or user');
      return { allComplete: false, completedCount: 0, expectedCount: 0 };
    }

    console.log('[MP] Notifying match complete');

    const response = await fetch(`${API_BASE}/${this.roomId}/match-complete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        playerId: this.currentUserId,
        totalMatchCount,
      }),
    });

    if (!response.ok) {
      console.error('[MP] Match complete notification failed');
      return { allComplete: false, completedCount: 0, expectedCount: 0 };
    }

    const result = await response.json();
    console.log('[MP] Match complete result:', result);
    return {
      allComplete: result.allComplete,
      completedCount: result.completedCount,
      expectedCount: result.expectedCount,
    };
  }

  // 全マッチ完了を待機 (繰り返しポーリング)
  async waitForAllMatchesComplete(maxWaitMs: number = 600000): Promise<boolean> {
    if (!this.roomId) return false;

    console.log('[MP] Waiting for all matches to complete (max', maxWaitMs / 1000, 'sec)...');

    const startTime = Date.now();
    const pollInterval = 30000; // 30秒ごとにポーリング

    while (Date.now() - startTime < maxWaitMs) {
      const remainingTime = maxWaitMs - (Date.now() - startTime);
      const pollTimeout = Math.min(pollInterval, remainingTime);

      const response = await fetch(
        `${API_BASE}/${this.roomId}/match-complete-poll?timeout=${pollTimeout}`
      );

      if (!response.ok) {
        console.error('[MP] Match complete poll failed');
        await this.delay(1000);
        continue;
      }

      const result = await response.json();
      console.log('[MP] Match complete poll result:', result);

      if (result.allComplete) {
        console.log('[MP] All matches completed!');
        return true;
      }

      // タイムアウトしたが全体の時間内なら再試行
      if (result.timeout && Date.now() - startTime < maxWaitMs) {
        console.log('[MP] Poll timeout, retrying...');
        continue;
      }
    }

    console.warn('[MP] Overall timeout waiting for all matches');
    return false;
  }

  // ========== ペアリング同期メソッド ==========

  // ペアリング情報を設定 (ホストのみ)
  async setPairings(pairings: MatchPairingSync[], bots: BotPlayerSync[]): Promise<boolean> {
    if (!this.roomId || !this.currentUserId) {
      console.error('[MP] Cannot set pairings: no room or user');
      return false;
    }

    if (!this.isHost) {
      console.error('[MP] Only host can set pairings');
      return false;
    }

    console.log('[MP] Setting pairings:', pairings.length, 'matches,', bots.length, 'bots');

    const response = await fetch(`${API_BASE}/${this.roomId}/set-pairings`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        playerId: this.currentUserId,
        pairings,
        bots,
      }),
    });

    if (!response.ok) {
      console.error('[MP] Set pairings failed');
      return false;
    }

    console.log('[MP] Pairings set successfully');
    return true;
  }

  // ペアリング情報を取得 (非ホスト用、ポーリング)
  async waitForPairings(maxWaitMs: number = 10000): Promise<{ pairings: MatchPairingSync[]; bots: BotPlayerSync[] } | null> {
    const startTime = Date.now();
    const checkIntervalMs = 1500;  // 1.5秒間隔 (KV API節約)

    console.log('[MP] Waiting for pairings from host...');

    while (Date.now() - startTime < maxWaitMs) {
      const roomState = await this.getRoomState();

      if (roomState?.pairings && roomState.pairings.length > 0) {
        console.log('[MP] Pairings received:', roomState.pairings.length, 'matches');
        return {
          pairings: roomState.pairings,
          bots: roomState.bots || [],
        };
      }

      await this.delay(checkIntervalMs);
    }

    console.warn('[MP] Timeout waiting for pairings');
    return null;
  }

  // マッチ準備完了を通知
  async notifyMatchReady(matchIndex: number): Promise<boolean> {
    if (!this.roomId || !this.currentUserId) return false;

    console.log('[MP] Notifying match ready: matchIndex=', matchIndex, 'playerId=', this.currentUserId);

    const response = await fetch(`${API_BASE}/${this.roomId}/match-ready`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        playerId: this.currentUserId,
        matchIndex,
      }),
    });

    return response.ok;
  }

  // 対戦相手のマッチ準備完了を待機
  async waitForOpponentMatchReady(matchIndex: number, opponentId: string, maxWaitMs: number = 15000): Promise<boolean> {
    if (!this.roomId) return false;

    const startTime = Date.now();
    const checkIntervalMs = 2000;  // 2秒間隔 (KV API節約)

    console.log('[MP] Waiting for opponent match ready: matchIndex=', matchIndex, 'opponentId=', opponentId);

    while (Date.now() - startTime < maxWaitMs) {
      const response = await fetch(`${API_BASE}/${this.roomId}/match-ready?matchIndex=${matchIndex}`);
      
      if (response.ok) {
        const result = await response.json();
        const readyPlayers = result.readyPlayers || [];
        
        if (readyPlayers.includes(opponentId)) {
          console.log('[MP] Opponent is ready for match: matchIndex=', matchIndex);
          return true;
        }
      }

      await this.delay(checkIntervalMs);
    }

    console.warn('[MP] Timeout waiting for opponent match ready: matchIndex=', matchIndex);
    return false;
  }

  // ペアリング受信完了を通知 (非ホスト用)
  async notifyPairingsAck(): Promise<boolean> {
    if (!this.roomId || !this.currentUserId) return false;

    console.log('[MP] Notifying pairings acknowledgment');

    const response = await fetch(`${API_BASE}/${this.roomId}/pairings-ack`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        playerId: this.currentUserId,
      }),
    });

    return response.ok;
  }

  // 全プレイヤーがペアリングを受信するまで待機 (ホスト用)
  async waitForPairingsAck(expectedCount: number, maxWaitMs: number = 10000): Promise<boolean> {
    if (!this.roomId) return false;

    const startTime = Date.now();
    const checkIntervalMs = 1000;

    console.log('[MP] Waiting for pairings ack from', expectedCount, 'players');

    while (Date.now() - startTime < maxWaitMs) {
      const response = await fetch(`${API_BASE}/${this.roomId}/pairings-ack`);
      
      if (response.ok) {
        const result = await response.json();
        const ackCount = result.ackPlayers?.length || 0;
        
        console.log('[MP] Pairings ack count:', ackCount, '/', expectedCount);
        
        if (ackCount >= expectedCount) {
          console.log('[MP] All players acknowledged pairings');
          return true;
        }
      }

      await this.delay(checkIntervalMs);
    }

    console.warn('[MP] Timeout waiting for pairings ack');
    return false;
  }

  // ========== マッチ結果同期メソッド ==========

  // マッチ結果を保存
  async saveMatchResult(matchResult: {
    matchIndex: number;
    winnerId: string;
    loserId: string;
    winnerFlags: number;
    round: number;
  }): Promise<boolean> {
    if (!this.roomId || !this.currentUserId) {
      console.error('[MP] Cannot save match result: no room or user');
      return false;
    }

    console.log('[MP] Saving match result:', matchResult.matchIndex, 'winner:', matchResult.winnerId);

    const response = await fetch(`${API_BASE}/${this.roomId}/match-results`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        playerId: this.currentUserId,
        matchResult,
      }),
    });

    if (!response.ok) {
      console.error('[MP] Failed to save match result');
      return false;
    }

    console.log('[MP] Match result saved successfully');
    return true;
  }

  // 全マッチ結果を取得
  async getMatchResults(): Promise<{
    matchIndex: number;
    winnerId: string;
    loserId: string;
    winnerFlags: number;
    round: number;
  }[]> {
    if (!this.roomId) return [];

    const response = await fetch(`${API_BASE}/${this.roomId}/match-results`);
    
    if (!response.ok) return [];
    
    const result = await response.json();
    return result.matchResults || [];
  }
}

// シングルトンインスタンス
let multiplayerInstance: MultiplayerManager | null = null;

export function getMultiplayerManager(): MultiplayerManager {
  if (!multiplayerInstance) {
    multiplayerInstance = new MultiplayerManager();
  }
  return multiplayerInstance;
}

export function resetMultiplayerManager(): void {
  if (multiplayerInstance) {
    multiplayerInstance.destroy();
    multiplayerInstance = null;
  }
}
